import { useEffect, type ReactNode } from "react";
import { X } from "lucide-react";

import { Button } from "@/components/ui/button";

/**
 * Minimal centered modal dialog: click-backdrop / Escape / close-button to
 * dismiss, scroll-locked while open. Kept dependency-free (no Radix) to stay
 * within the SPA/artifact CSP + bundle budget.
 */
export function Modal({
  title,
  onClose,
  children,
  className,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  className?: string;
}) {
  useEffect(() => {
    // Captured and stopped, so Escape closes this modal only and not a sheet
    // or drawer it was opened from, which listens on the document too.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener("keydown", onKey, true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop is `fixed`, not `absolute`: the `p-4` on this container is the
          dialog's centering gutter, and an `absolute inset-0` child would inset
          to the padding box, leaving an uncovered ~16px band on every edge (the
          bright header showed through at the top). `fixed inset-0` pins it to the
          viewport instead so it covers the full screen. */}
      <button
        type="button"
        aria-label={title}
        className="fixed inset-0 bg-black/40"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={
          "relative z-10 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-background shadow-xl " +
          (className ?? "")
        }
      >
        <div className="flex items-start justify-between gap-4 border-b border-border p-4">
          <h2 className="text-lg font-semibold">{title}</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X aria-hidden />
            <span className="sr-only">{title}</span>
          </Button>
        </div>
        <div className="overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}
